import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Row, Col, Button, Spinner, Form, Card, Alert, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faSync, 
  faArrowUp, 
  faArrowDown, 
  faMinus,
  faTachometerAlt,
  faMapMarkerAlt,
  faCube,
  faChartLine
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import Sidebar from '../../../component/sidebar/sidebar';
import Header from '../../../component/header/header';
// import { ChartFactory } from '../../AIDashboard/AIDashboard/utils';
import { ChartFactory } from '../../ChartUtility/charts';
import { useMeter, useGraphData } from './hooks/useMeters';
import { buildWidgetConfig } from './services/meterApi';
import { useFinancialYears, useSources } from '../../../hooks/useApiData';
import './styles/MeterDetails.css';

const MeterDetails = () => {
  const { meterId } = useParams();
  const history = useHistory();
  const sources = useSources();
  const financialYears = useFinancialYears();
  const currentFY = financialYears.sort(fy => fy.financial_year_value)?.[0]?.financial_year_value;

  // Auto-refresh state
  const [lastRefreshTime, setLastRefreshTime] = useState(moment());

  // Fetch meter details
  const { data: meter, isLoading: meterLoading, error: meterError, refetch: refetchMeter } = useMeter(meterId);

  const currentSource = sources?.find(s => s.id == meter?.sourceId);
  const currentSubLocation = currentSource?.subLocation?.find(sl => sl.id == meter?.subLocationId)

  // Get current financial year KPI
  const currentFYKpi = useMemo(() => {
    if (!meter?.financialYearKpis) return null;
    return meter.financialYearKpis.find(mapping => mapping.financialYear === currentFY);
  }, [meter]);

  const handleManualRefresh = () => {
    refetchMeter();
    setLastRefreshTime(moment());
  };

  if (meterLoading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading meter details...</p>
      </div>
    );
  }

  if (meterError || !meter) {
    return (
      <div className="text-center p-5">
        <Alert variant="danger">
          Failed to load meter details. Please try again.
        </Alert>
        <Button variant="primary" onClick={() => history.push('/iot-meters')}>
          Back to Meters
        </Button>
      </div>
    );
  }

  if (!currentFYKpi) {
    return (
      <div className="meter-details-page">
        <Row className="flex-nowrap m-0 p-0">
          <Col xs={2} className="sidebar-container p-0">
            <Sidebar activePage="iot-meters" />
          </Col>
          
          <Col xs={10} className="content-area p-0">
            <div style={{ position: 'sticky', top: 0, zIndex: 999 }}>
              <Header />
            </div>
            
            <div className="content-wrapper">
              <div className="page-header">
                <Button 
                  variant="outline-secondary" 
                  size="sm"
                  onClick={() => history.push('/iot-meters')}
                  className="mb-3"
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                  Back to Meters
                </Button>
                
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h1>{meter.meterName}</h1>
                    <p className="mb-0">
                      {meter.meterType} • {meter.unit}
                    </p>
                  </div>
                </div>
              </div>

              <Alert variant="warning" className="mt-4">
                <Alert.Heading>KPI Configuration Required</Alert.Heading>
                <p>
                  No KPI has been configured for this meter in the current financial year ({currentFY}).
                  Please configure a KPI to view analytics and consumption data.
                </p>
                <hr />
                <div className="d-flex justify-content-end">
                  <Button 
                    variant="outline-warning"
                    onClick={() => history.push('/iot-meters')}
                  >
                    Go to Meters List to Configure KPI
                  </Button>
                </div>
              </Alert>

              <Card className="mt-3">
                <Card.Header>
                  <h5>Meter Information</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <p><strong>Meter ID:</strong> {meter.meterId}</p>
                      <p><strong>Gateway ID:</strong> {meter.gatewayId}</p>
                      <p><strong>Meter Type:</strong> {meter.meterType}</p>
                      <p><strong>Measurement Type:</strong> {meter.measurementType}</p>
                    </Col>
                    <Col md={6}>
                      <p><strong>Unit:</strong> {meter.unit}</p>
                      <p><strong>Location:</strong> {currentSource?.unitCode || currentSource?.location?.area || meter.sourceId}</p>
                      <p><strong>Sub Location:</strong> {currentSubLocation?.subLocation || meter.subLocationId}</p>
                      <p><strong>Status:</strong> {meter.isActive ? 'Active' : 'Inactive'}</p>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="meter-details-page">
      <Row className="flex-nowrap m-0 p-0">
        <Col xs={2} className="sidebar-container p-0">
          <Sidebar activePage="iot-meters" />
        </Col>
        
        <Col xs={10} className="content-area p-0">
          <div style={{ position: 'sticky', top: 0, zIndex: 999 }}>
            <Header />
          </div>
          
          <div className="content-wrapper">
            {/* Page Header */}
            <div className="page-header-new">
              <Button 
                variant="link" 
                className="back-button"
                onClick={() => history.push('/iot-meters')}
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Back to Meters
              </Button>
              
              <div className="header-controls">
                <div className="refresh-info">
                  <Button 
                    variant="link" 
                    className="refresh-icon-btn"
                    onClick={handleManualRefresh}
                    title="Refresh graphs"
                  >
                    <FontAwesomeIcon icon={faSync} />
                  </Button>
                  <span className="last-refresh">
                    Last updated: {lastRefreshTime.format('HH:mm:ss')}
                  </span>
                </div>
              </div>
            </div>

            <div className="meter-name-hero">
              <div className="meter-name-content">
                <div className="meter-header-row">
                  <h1 className="meter-name">{meter.meterName}</h1>
                  <span className={`meter-status-badge ${meter.isActive ? 'active' : 'inactive'}`}>
                    <span className="status-dot"></span>
                    {meter.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
            {/* Info Cards Section */}
            <div className="info-cards-grid">
              <InfoCard
                icon={faTachometerAlt}
                label="Meter ID"
                value={meter.meterId}
                subValue={meter.meterName}
                color="blue"
              />
              <InfoCard
                icon={faMapMarkerAlt}
                label="Location"
                value={`${currentSource?.unitCode || currentSource?.location?.area || meter.id}${currentSubLocation?.subLocation || meter.subLocationId ?  `-${currentSubLocation?.subLocation || meter.subLocationId}` : ''}`}
                color="green"
              />
              <InfoCard
                icon={faCube}
                label="Module"
                value={currentFYKpi.kpi.module}
                subValue={`Unit: ${meter.unit}`}
                color="purple"
              />
              <InfoCard
                icon={faChartLine}
                label="KPI Mapping"
                value={currentFYKpi.kpi.category}
                subValue={`${currentFYKpi.kpi.subCategory} → ${currentFYKpi.kpi.kpi}`}
                color="orange"
              />
            </div>

            {/* Quick Stats Section */}
            <QuickStatsSection 
              meter={meter}
              currentFY={currentFY}
              currentFYKpi={currentFYKpi}
            />

            {/* Live Graphs Section */}
            <div className="graphs-section">
              <h2 className="section-title">Live Monitoring</h2>
              
              {/* 1. Instantaneous Flow */}
              <GraphCard
                currentFY={currentFY}
                title="Instantaneous Flow"
                description="Real-time flow rate measurements"
                meter={meter}
                currentFYKpi={currentFYKpi}
                graphType="instantaneousFlow"
                autoRefresh={true}
                onRefresh={() => setLastRefreshTime(moment())}
              />

              {/* 2. Mass Totalizer */}
              <GraphCard
                currentFY={currentFY}
                title="Volume Totalizer"
                description="Cumulative volume flow total"
                meter={meter}
                currentFYKpi={currentFYKpi}
                graphType="massTotalizer"
                autoRefresh={true}  // ✅ Enabled
                onRefresh={() => setLastRefreshTime(moment())}
              />

              {/* 3. Net Consumption */}
              <GraphCard
                currentFY={currentFY}
                title="Net Consumption"
                description="Total consumption over time"
                meter={meter}
                currentFYKpi={currentFYKpi}
                graphType="netConsumption"
                aggregationPeriod='day'
                timeRangePeriod='week'
              />
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

// Info Card Component
const InfoCard = ({ icon, label, value, subValue, color }) => {
  return (
    <div className={`info-card info-card-${color}`}>
      <div className="info-card-icon">
        <FontAwesomeIcon icon={icon} />
      </div>
      <div className="info-card-content">
        <span className="info-card-label">{label}</span>
        <h3 className="info-card-value">{value}</h3>
        <span className="info-card-subvalue">{subValue}</span>
      </div>
    </div>
  );
};

// Quick Stats Section Component
const QuickStatsSection = ({ meter, currentFY, currentFYKpi }) => {
  // Build configs for quick stats (fetch both periods in single call)
  const quickStatsConfigs = useMemo(() => {
    if (!meter || !currentFYKpi) return null;

    const baseParams = {
      meterId: meter.meterId,
      financialYear: currentFY,
      locationId: meter.sourceId?.toString() || '1',
      module: currentFYKpi.kpi.module,
      category: currentFYKpi.kpi.category,
      subCategory: currentFYKpi.kpi.subCategory,
      kpi: currentFYKpi.kpi.kpi,
      kpiType: 'EXTERNAL',
      xAxisField: 'displayPeriods'
    };

    return {
      // Fetch today + yesterday in one call
      daily: buildWidgetConfig({
        ...baseParams,
        startDateTime: moment().subtract(2, 'days').startOf('day').toISOString(), // Yesterday 00:00:00 UTC
        endDateTime: moment().toISOString(), // Now UTC
        aggregationPeriodValue: 1,
        aggregationPeriodUnit: 'days',
      }),
      // Fetch this week + last week in one call
      weekly: buildWidgetConfig({
        ...baseParams,
        startDateTime: moment().subtract(2, 'weeks').startOf('week').toISOString(), // 2 weeks ago UTC
        endDateTime: moment().toISOString(), // Now UTC
        aggregationPeriodValue: 1,
        aggregationPeriodUnit: 'weeks',
      }),
      // Fetch this month + last month in one call
      monthly: buildWidgetConfig({
        ...baseParams,
        startDateTime: moment().subtract(2, 'months').startOf('month').toISOString(), // 2 months ago UTC
        endDateTime: moment().toISOString(), // Now UTC
        aggregationPeriodValue: 1,
        aggregationPeriodUnit: 'months',
      }),
    };
  }, [meter, currentFYKpi, currentFY]);

  // Fetch data
  const { data: dailyData, isLoading: dailyLoading } = useGraphData(
    quickStatsConfigs?.daily, 
    { enabled: !!quickStatsConfigs }
  );
  const { data: weeklyData, isLoading: weeklyLoading } = useGraphData(
    quickStatsConfigs?.weekly, 
    { enabled: !!quickStatsConfigs }
  );
  const { data: monthlyData, isLoading: monthlyLoading } = useGraphData(
    quickStatsConfigs?.monthly, 
    { enabled: !!quickStatsConfigs }
  );

  // Process daily stats
  const dayStats = useMemo(() => {
    if (!dailyData?.data) return null;

    const dataArray = Array.isArray(dailyData.data) ? dailyData.data : dailyData.data.data;
    if (!dataArray) return null;

    const today = moment().startOf('day');
    const yesterday = moment().subtract(1, 'day').startOf('day');

    const todayItem = dataArray.find(item => 
      item.period_ts && moment(item.period_ts).isSame(today, 'day')
    );
    const yesterdayItem = dataArray.find(item => 
      item.period_ts && moment(item.period_ts).isSame(yesterday, 'day')
    );

    const todayValue = todayItem ? Number(todayItem.sum_value || 0) : 0;
    const yesterdayValue = yesterdayItem ? Number(yesterdayItem.sum_value || 0) : 0;
    const change = yesterdayValue ? ((todayValue - yesterdayValue) / yesterdayValue) * 100 : 0;
    const changeValue = todayValue - yesterdayValue;

    return { value: todayValue, change, changeValue };
  }, [dailyData]);

  // Process weekly stats
  const weekStats = useMemo(() => {
    if (!weeklyData?.data) return null;

    const dataArray = Array.isArray(weeklyData.data) ? weeklyData.data : weeklyData.data.data;
    if (!dataArray) return null;

    const thisWeekStart = moment().startOf('week');
    const lastWeekStart = moment().subtract(1, 'week').startOf('week');

    const thisWeekItem = dataArray.find(item => 
      item.period_ts && moment(item.period_ts).isSame(thisWeekStart, 'week')
    );
    const lastWeekItem = dataArray.find(item => 
      item.period_ts && moment(item.period_ts).isSame(lastWeekStart, 'week')
    );

    const thisWeekValue = thisWeekItem ? Number(thisWeekItem.sum_value || 0) : 0;
    const lastWeekValue = lastWeekItem ? Number(lastWeekItem.sum_value || 0) : 0;
    const change = lastWeekValue ? ((thisWeekValue - lastWeekValue) / lastWeekValue) * 100 : 0;
    const changeValue = thisWeekValue - lastWeekValue;

    return { value: thisWeekValue, change, changeValue };
  }, [weeklyData]);

  // Process monthly stats
  const monthStats = useMemo(() => {
    if (!monthlyData?.data) return null;

    const dataArray = Array.isArray(monthlyData.data) ? monthlyData.data : monthlyData.data.data;
    if (!dataArray) return null;

    const thisMonthStart = moment().startOf('month');
    const lastMonthStart = moment().subtract(1, 'month').startOf('month');

    const thisMonthItem = dataArray.find(item => 
      item.period_ts && moment(item.period_ts).isSame(thisMonthStart, 'month')
    );
    const lastMonthItem = dataArray.find(item => 
      item.period_ts && moment(item.period_ts).isSame(lastMonthStart, 'month')
    );

    const thisMonthValue = thisMonthItem ? Number(thisMonthItem.sum_value || 0) : 0;
    const lastMonthValue = lastMonthItem ? Number(lastMonthItem.sum_value || 0) : 0;
    const change = lastMonthValue ? ((thisMonthValue - lastMonthValue) / lastMonthValue) * 100 : 0;
    const changeValue = thisMonthValue - lastMonthValue;

    return { value: thisMonthValue, change, changeValue };
  }, [monthlyData]);

  const isLoading = dailyLoading || weeklyLoading || monthlyLoading;

  if (isLoading || !dayStats || !weekStats || !monthStats) {
    return (
      <div className="quick-stats-section">
        <h2 className="section-title">Quick Stats</h2>
        <div className="text-center p-4">
          <Spinner animation="border" variant="primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="quick-stats-section">
      <h2 className="section-title">Quick Stats</h2>
      <div className="quick-stats-grid">
        <StatCard
          title="Today"
          value={dayStats.value}
          unit={meter.unit}
          change={dayStats.change}
          changeValue={dayStats.changeValue}
          comparison="vs Yesterday"
        />
        <StatCard
          title="This Week"
          value={weekStats.value}
          unit={meter.unit}
          change={weekStats.change}
          changeValue={weekStats.changeValue}
          comparison="vs Last Week"
        />
        <StatCard
          title="This Month"
          value={monthStats.value}
          unit={meter.unit}
          change={monthStats.change}
          changeValue={monthStats.changeValue}
          comparison="vs Last Month"
        />
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, unit, change, changeValue, comparison }) => {
  const getChangeIcon = () => {
    if (change > 0) return faArrowUp;
    if (change < 0) return faArrowDown;
    return faMinus;
  };

  const getChangeClass = () => {
    if (change > 0) return 'positive';
    if (change < 0) return 'negative';
    return 'neutral';
  };

  return (
    <div className="stat-card">
      <h4 className="stat-title">{title}</h4>
      <div className="stat-value">
        {value.toLocaleString(undefined, { maximumFractionDigits: 2 })}
        <span className="stat-unit">{unit}</span>
      </div>
      <div className={`stat-change ${getChangeClass()}`}>
        <FontAwesomeIcon icon={getChangeIcon()} className="me-1" />
        <span className="change-percent">{Math.abs(change).toFixed(1)}%</span>
        <span className="change-value">
          ({change > 0 ? '+' : ''}{changeValue.toFixed(2)} {unit})
        </span>
      </div>
      <span className="stat-comparison">{comparison}</span>
    </div>
  );
};

// Graph Card Component with individual filters
const GraphCard = ({ 
  currentFY,
  title, 
  description, 
  meter, 
  currentFYKpi, 
  graphType, 
  autoRefresh, 
  aggregationPeriod = 'hour',
  timeRangePeriod = 'day'
}) => {
  // Individual state for each graph
  const [timeRange, setTimeRange] = useState(timeRangePeriod);  // ✅ Use prop
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [aggregation, setAggregation] = useState(aggregationPeriod);  // ✅ Use prop

  // Track what was changed last to prevent infinite loops
  const lastChanged = React.useRef(null);  // ✅ Changed from 'timeRange' to null
  const isInitialMount = React.useRef(true);  // ✅ Track initial mount

  // Bidirectional sync between time range and aggregation
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (lastChanged.current === 'timeRange') {
      // Time range changed → update aggregation
      const defaultAggregations = {
        'hour': 'minute',
        'day': 'hour',
        'week': 'day',
        'month': 'day',
        'custom': 'hour'
      };
      setAggregation(defaultAggregations[timeRange]);
    }
  }, [timeRange]);

  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount.current) {
      return;
    }

    if (lastChanged.current === 'aggregation') {
      // Aggregation changed → update time range
      const defaultTimeRanges = {
        'minute': 'hour',
        'hour': 'day',
        'day': 'week',
        'month': 'month'
      };
      const newTimeRange = defaultTimeRanges[aggregation];
      if (newTimeRange && timeRange !== 'custom') {
        setTimeRange(newTimeRange);
      }
    }
  }, [aggregation, timeRange]);

  // Handlers that track what was changed
  const handleTimeRangeChange = (e) => {
    lastChanged.current = 'timeRange';
    setTimeRange(e.target.value);
  };

  const handleAggregationChange = (e) => {
    lastChanged.current = 'aggregation';
    setAggregation(e.target.value);
  };

  // Calculate date range based on selection
  const dateRange = useMemo(() => {
    const now = moment();
    switch (timeRange) {
      case 'hour':
        return {
          start: moment().subtract(1, 'hour'),
          end: now
        };
      case 'day':
        return {
          start: moment().subtract(1, 'day'),
          end: now
        };
      case 'week':
        return {
          start: moment().subtract(7, 'days'),
          end: now
        };
      case 'month':
        return {
          start: moment().subtract(1, 'month'),
          end: now
        };
      case 'custom':
        return {
          start: customStart || moment().subtract(7, 'days'),
          end: customEnd || now
        };
      default:
        return {
          start: moment().subtract(1, 'day'),
          end: now
        };
    }
  }, [timeRange, customStart, customEnd]);

  // ✅ Dynamic title with units and aggregation
  const displayTitle = useMemo(() => {
    // Define units for each graph type
    const graphUnits = {
      'instantaneousFlow': 'm³/h',
      'massTotalizer': 'm³',
      'netConsumption': meter?.unit || ''
    };

    const baseTitle = title;
    const unit = graphUnits[graphType];

    // For Net Consumption, show aggregation and unit
    if (graphType === 'netConsumption') {
      const aggregationLabels = {
        'minute': 'Per Minute',
        'hour': 'Hourly',
        'day': 'Daily',
        'month': 'Monthly'
      };
      return unit 
        ? `${baseTitle} (${aggregationLabels[aggregation]}) - ${unit}`
        : `${baseTitle} (${aggregationLabels[aggregation]})`;
    }

    // For other graphs, just show unit in parentheses
    return unit ? `${baseTitle} (${unit})` : baseTitle;
  }, [title, graphType, aggregation, meter?.unit]);

  const isABBMassTotalizer = meter.meterType === 'ABB_WATER_FLOW_METER' && graphType === 'massTotalizer';

  // Build graph config based on type
  const graphConfig = useMemo(() => {
    if (!meter || !currentFYKpi) return null;

    const baseParams = {
      meterId: meter.meterId,
      financialYear: currentFY,
      startDateTime: dateRange.start.toISOString(),
      endDateTime: dateRange.end.toISOString(),
      locationId: meter.sourceId?.toString() || '1',
      module: currentFYKpi.kpi.module,
      aggregationPeriodValue: 1,
      aggregationPeriodUnit: getAggregationUnit(aggregation),
      chartType: 'line'
    };

    switch (graphType) {
      case 'instantaneousFlow':
        return buildWidgetConfig({
          ...baseParams,
          category: 'Flow',
          subCategory: 'Rate',
          kpi: 'Volume Flow',
          kpiType: 'INTERNAL',
          measurementType: 'INSTANT',
          title: 'Instantaneous Flow'
        });
      
      case 'massTotalizer':
        return buildWidgetConfig({
          ...baseParams,
          enableStacking: true,
          stackByField: 'kpi',
          category: 'Mass Totalizer',
          kpiType: 'INTERNAL',
          measurementType: 'INSTANT',
          title: 'Mass Totalizer'
        });
      
      case 'netConsumption':
        return buildWidgetConfig({
          ...baseParams,
          category: currentFYKpi.kpi.category,
          subCategory: currentFYKpi.kpi.subCategory,
          kpi: currentFYKpi.kpi.kpi,
          kpiType: 'EXTERNAL',
          title: 'Net Consumption'
        });
      
      default:
        return null;
    }
  }, [meter, currentFYKpi, dateRange, aggregation, graphType]);

  // Fetch graph data
  const { data: graphData, isLoading, refetch } = useGraphData(
    graphConfig,
    { enabled: !!graphConfig }
  );

    const transformedGraphData = useMemo(() => {
    const data = graphData?.data || [];
    if (!data.length) return data;

    return data.map((series) => {
      if (series.kpi === 'Reverse Flow') {
        return {
          ...series,
          value: isNaN(Number(series.value)) ? 0 : -Number(series.value),
          sum_value: isNaN(Number(series.value)) ? 0 : -Number(series.value),
        };
      }
      return series;
    });
  }, [graphData]);

  // Auto-refresh for this graph
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      refetch();
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, refetch]);

  return (
    <Card className="graph-card">
      <Card.Header>
        <div className="graph-card-header">
          <div>
            <h5 className="mb-0">{displayTitle}</h5>
            <small className="text-muted">{description}</small>
          </div>
        </div>
      </Card.Header>
      <Card.Body>
        {/* Graph Controls */}
        <div className="graph-controls">
          <Row className="align-items-end">
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small">Time Range</Form.Label>
                <Form.Select
                  size="sm"
                  value={timeRange}
                  onChange={handleTimeRangeChange} 
                >
                  <option value="hour">Last Hour</option> 
                  <option value="day">Last 24 Hours</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                  <option value="custom">Custom Range</option>
                </Form.Select>
              </Form.Group>
            </Col>

            {timeRange === 'custom' && (
              <>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">Start Date</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      size="sm"
                      value={customStart ? customStart.format('YYYY-MM-DDTHH:mm') : ''}
                      onChange={(e) => setCustomStart(moment(e.target.value))}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">End Date</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      size="sm"
                      value={customEnd ? customEnd.format('YYYY-MM-DDTHH:mm') : ''}
                      onChange={(e) => setCustomEnd(moment(e.target.value))}
                    />
                  </Form.Group>
                </Col>
              </>
            )}

            <Col md={3}>
              <Form.Group>
                <Form.Label className="small">Aggregation</Form.Label>
                <Form.Select
                  size="sm"
                  value={aggregation}
                  onChange={handleAggregationChange}
                >
                  <option value="minute">Per Minute</option>
                  <option value="hour">Hourly</option>
                  <option value="day">Daily</option>
                  <option value="month">Monthly</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </div>

        {/* Graph */}
        <div className="graph-container">
          {isLoading ? (
            <div className="text-center p-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <ChartFactory
              chartData={{
                data: transformedGraphData,
                chartType: 'line',
                title: title
              }}
              chartOptions={{
                height: 350,
                stacked: false,
                transform: true
              }}
              group_by={['displayPeriods', graphConfig.stackByField]}
            />
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

// Helper function to get total from graph data
const getTotalFromData = (response) => {
  if (!response?.data?.data || !Array.isArray(response.data.data)) {
    return 0;
  }

  return response.data.data.reduce(
    (sum, item) => sum + (Number(item.sum_value) || 0),
    0
  );
};


// Helper function to map aggregation to unit
const getAggregationUnit = (aggregation) => {
  const map = {
    minute: 'minutes',
    hour: 'hours',
    day: 'days',
    week: 'weeks',
    month: 'months'
  };
  return map[aggregation] || 'hours';
};

export default MeterDetails;